import { format } from 'date-fns';
import type { ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { InquiryForwardCard } from './InquiryForwardCard';
import { LinkPreviewCard } from './LinkPreviewCard';
import { ReferencedInquiryCard } from './ReferencedInquiryCard';
import { SharedSurveyCard } from './SharedSurveyCard';
import { SupportMessageAttachments } from './SupportMessageAttachments';
import { previewableLinks } from './supportLinks';
import type { ThreadFile } from './threadItems';

import { primum_default_avatar } from '@/assets/images';
import { SendStatusLine } from '@/components/SendStatusLine';
import { SELECTABLE_TEXT_PROPS } from '@/components/libraryChat/selectableText';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { InquiryForward, InquiryForwardTarget } from '@/services/conversations/inquiryForward';
import type { ReferencedInquiry } from '@/services/conversations/referencedInquiry';
import type { SharedSurvey } from '@/services/conversations/sharedSurvey';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import {
  findMessageLinks,
  type MessageLinkKind,
  openMessageLink,
  toMessageLinkHref,
} from '@/util/messageLinks';

/** The link-sized actions are about 20px tall; the slop brings them to the minimum target. */
const LINK_HIT_SLOP = getAccessibleHitSlop(20);

/**
 * The message text with its links, phone numbers and addresses drawn as
 * underlined runs that open on press. Each takes the message's text classes,
 * since a nested Text brings its own gray, which would vanish on the brand
 * fill.
 */
function bodyWithLinks(
  body: string,
  textClassName: string,
  onOpenLink: (href: string, kind: MessageLinkKind) => void
): ReactNode[] {
  const nodes: ReactNode[] = [];

  let cursor = 0;

  for (const link of findMessageLinks(body)) {
    if (link.start > cursor) {
      nodes.push(body.slice(cursor, link.start));
    }

    nodes.push(
      <Text
        key={link.start}
        className={twJoin(textClassName, 'underline web:cursor-pointer')}
        onPress={() => onOpenLink(toMessageLinkHref(link), link.kind)}
        accessibilityRole="link">
        {link.text}
      </Text>
    );

    cursor = link.end;
  }

  if (cursor < body.length) {
    nodes.push(body.slice(cursor));
  }

  return nodes;
}

export type SupportMessageStatus = 'sent' | 'pending' | 'failed';

type SupportMessageBubbleProps = {
  body: string;
  createdAt: string;
  isOwn: boolean;
  /** Who wrote it, shown above the other party's messages only. */
  authorLabel?: string;
  /** The other party's picture, beside their messages; the default avatar when absent. */
  avatarUrl?: string;
  status: SupportMessageStatus;
  hasReference: boolean;
  onOpenReference?: () => void;
  inquiryForward?: InquiryForward | null;
  onOpenForwardInquiry?: (target: InquiryForwardTarget) => void;
  onRetry?: () => void;
  onDiscard?: () => void;
  /** Why a failed message was refused, when the reader can act on it; shown after "Not sent". */
  failureReason?: string;
  /** The linked inquiry's details, shown as a card in place of the plain link. */
  referencedInquiry?: ReferencedInquiry | null;
  /** The survey the message shares, shown as a card the member opens to take it. */
  sharedSurvey?: SharedSurvey | null;
  onOpenSurvey?: () => void;
  /** Opens a pressed link; by default the system handles it. */
  onOpenLink?: (href: string, kind: MessageLinkKind) => void;
  /** Files sent with the message, under its text. */
  files?: ThreadFile[];
};

function timeLabel(createdAt: string): string {
  const date = new Date(createdAt);

  return Number.isNaN(date.getTime()) ? '' : format(date, 'h:mm a');
}

/**
 * One message in a support thread. The reader's own messages sit on the right
 * in the brand fill, the other party's on the left on the card surface, the
 * same split Research uses for the reader's question.
 *
 * The other party's messages carry their avatar, as support messages in the
 * inquiry chat always have.
 *
 * A pending message is the reader's own send that has not been confirmed yet;
 * it renders like a sent one with a quieter caption, since most land within a
 * second. A failed one says so in words, not by color alone, and offers
 * Delete, and Retry unless the refusal would only repeat.
 *
 * A forward note's reference is the forwarded inquiry itself, so its card
 * stands in for the reference link rather than offering the same door twice.
 *
 * Links in the text, such as one added from the attach menu, open through
 * `onOpenLink` (the thread sends a catalog paper to the reference sheet and
 * anything else to the in-app browser), drawn underlined in the text's own color so they read on either
 * fill.
 *
 * Each web link in the text, up to a few, also unfurls into a preview card
 * under it, as in Slack and iMessage; the link stays in the text too.
 *
 * A message sent with only files has no text, so the text line is left out
 * rather than drawn empty above them.
 */
export function SupportMessageBubble({
  body,
  createdAt,
  isOwn,
  authorLabel,
  avatarUrl,
  status,
  hasReference,
  onOpenReference,
  inquiryForward,
  onOpenForwardInquiry,
  onRetry,
  onDiscard,
  failureReason,
  referencedInquiry,
  sharedSurvey,
  onOpenSurvey,
  files = [],
  onOpenLink = openMessageLink,
}: SupportMessageBubbleProps) {
  const bodyClassName = twJoin(
    'text-base',
    isOwn ? 'text-typography-white' : 'text-typography-900'
  );

  const caption = status === 'pending' ? 'Sending…' : timeLabel(createdAt);

  const forwardCard =
    inquiryForward && onOpenForwardInquiry ? (
      <InquiryForwardCard forward={inquiryForward} onOpen={onOpenForwardInquiry} />
    ) : null;

  return (
    <HStack className="w-full items-end gap-2 px-4 py-1">
      {isOwn ? null : (
        <Avatar size="sm" className="mb-5" aria-hidden>
          <AvatarImage source={avatarUrl ? { uri: avatarUrl } : primum_default_avatar} />
        </Avatar>
      )}

      <VStack className={twJoin('min-w-0 flex-1', isOwn ? 'items-end' : 'items-start')}>
        {!isOwn && authorLabel ? (
          <Text className="mb-1 px-1 font-semibold text-xs text-typography-700">{authorLabel}</Text>
        ) : null}

        <VStack
          className={twJoin(
            'max-w-[85%] rounded-2xl px-4 py-3',
            isOwn
              ? 'rounded-br-sm bg-primary-500'
              : 'rounded-bl-sm border border-outline-200 bg-background-0'
          )}>
          {body ? (
            <Text className={bodyClassName} {...SELECTABLE_TEXT_PROPS}>
              {bodyWithLinks(body, bodyClassName, onOpenLink)}
            </Text>
          ) : null}

          {previewableLinks(body).map((url) => (
            <LinkPreviewCard key={url} url={url} onOpen={(link) => onOpenLink(link, 'url')} />
          ))}

          <SupportMessageAttachments files={files} />

          {forwardCard}

          {referencedInquiry && !forwardCard ? (
            <ReferencedInquiryCard inquiry={referencedInquiry} onOpen={onOpenReference} />
          ) : null}

          {sharedSurvey ? (
            <SharedSurveyCard
              survey={sharedSurvey}
              onOpen={status === 'sent' ? onOpenSurvey : undefined}
            />
          ) : null}

          {hasReference && onOpenReference && !forwardCard && !referencedInquiry ? (
            <Button
              variant="link"
              size="sm"
              className="mt-1 h-auto self-start p-0"
              hitSlop={LINK_HIT_SLOP}
              onPress={onOpenReference}
              accessibilityLabel="Open the linked inquiry">
              <ButtonText
                className={twJoin(
                  'underline',
                  isOwn ? 'text-typography-white' : 'text-primary-700'
                )}>
                View linked inquiry
              </ButtonText>
            </Button>
          ) : null}
        </VStack>

        <SendStatusLine
          status={status}
          caption={caption}
          failureReason={failureReason}
          onRetry={onRetry}
          onDiscard={onDiscard}
        />
      </VStack>
    </HStack>
  );
}
